/* eslint-disable jest/require-hook */
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import styled from "styled-components";
import { Navigation, Pagination } from "swiper";
import swiperNavCss from "swiper/css/navigation?raw";
import swiperPaginationCss from "swiper/css/pagination?raw";
import swiperCss from "swiper/css?raw";
import { Swiper, SwiperSlide } from "swiper/react";

import { PLACEHOLDER_IMAGE } from "./constants";
import { FeaturedProductItem } from "./FeaturedProductItem";
import localCss from "./style.css?raw";
import { SwiperButton } from "./SwiperButton";
import { AppletProps, ShopifyCollection, WidgetProduct } from "./types";
import { getCollectionInformation } from "./utils";

import { isAppletPicker, isDevelopment } from "~/env";
import { useFlag } from "~/LaunchDarkly";
import { isTruthy } from "~/utils";

const StyledWrapper = styled.div`
  ${swiperNavCss}
  ${swiperPaginationCss}
  ${swiperCss}
  ${localCss}
  padding: 20px;
`;

export const Applet: React.FC<AppletProps> = ({
  collectionId,
  fourMode = false,
  title,
}) => {
  const hasAuthorization = useFlag("applet-es-shopify-featured-products");

  const [widgetProducts, setWidgetProducts] = useState<Array<WidgetProduct>>(
    []
  );

  const [information, setInformation] = useState<ShopifyCollection | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (hasAuthorization) {
      setWidgetProducts(
        Array.from(document.getElementsByClassName("featured-widget-products"))
          .map((el) => {
            if (el.textContent) {
              const [title, handle, image] = el.textContent
                .trim()
                .split("\n")
                .map((str) => str.trim());

              el.remove();

              return {
                title,
                handle,
                image,
              };
            }

            return null;
          })
          .filter(isTruthy)
      );

      getCollectionInformation(collectionId).then(({ information }) => {
        setInformation(information);
        setIsLoading(false);
      });
    }
  }, ["hasAuthorization", "collectionId"]);

  if (!hasAuthorization || !widgetProducts.length) {
    return null;
  }

  return (
    <StyledWrapper>
      {!fourMode && (
        <div className="featured-collection-img">
          {!isLoading && (
            <img
              src={information?.image.url || PLACEHOLDER_IMAGE}
              alt="Featured Collection"
            />
          )}
        </div>
      )}

      {!fourMode && <hr className="line" />}

      <div
        className={`featured-collection-slider${!fourMode ? "" : "-fourMode"}`}
      >
        <div style={{ display: "block", width: "100%" }}>
          <div
            className={`slider-title-${!fourMode ? "feat" : "cart"}`}
            style={{ textAlign: "center" }}
          >
            {title || "Other Products of Interest"}
          </div>

          <div className="carousel">
            <SwiperButton direction="left" />
            <Swiper
              modules={[Navigation, Pagination]}
              pagination={{
                el: ".pagination-bullets",
                type: "bullets",
              }}
              slidesPerView={2}
              spaceBetween={30}
              breakpoints={{
                720: {
                  slidesPerView: !fourMode ? 3 : 4,
                  navigation: {
                    nextEl: ".right",
                    prevEl: ".left",
                  },
                },
              }}
            >
              {widgetProducts.map((product) => (
                <SwiperSlide key={product.handle}>
                  {" "}
                  <FeaturedProductItem product={product} />
                </SwiperSlide>
              ))}
            </Swiper>
            <SwiperButton direction="right" />
          </div>
        </div>
      </div>
      <div className="pagination-bullets"></div>
      {!fourMode && <hr className="line" />}
    </StyledWrapper>
  );
};

export const mount = async (el: HTMLDivElement) => {
  if (isDevelopment || isAppletPicker) {
    const fixtures = await import("./fixtures");

    el.setAttribute("class", "collection-featured-products-widget");
    el.setAttribute("collectionid", "279022141602");
    el.setAttribute("title", "Essential Parts");

    const elProducts = document.createElement("div");
    elProducts.innerHTML = fixtures.FEATURED_PRODUCTS_HTML;
    document.body.appendChild(elProducts);
  }

  const containers = document.querySelectorAll(
    ".collection-featured-products-widget[collectionid]"
  );

  Array.from(containers).forEach((el) => {
    const collectionId = el.getAttribute("collectionid");

    const root = createRoot(el);

    root.render(
      <Applet
        collectionId={`gid://shopify/Collection/${collectionId}`}
        title={el.getAttribute("title") || ""}
        fourMode={el.hasAttribute("fourMode")}
      />
    );
  });
};
